// Title screen: game name, the top-10 high-score table, and Play / Help / Mute
// controls. Reads persisted scores and mute setting; never runs the sim.

import Phaser from 'phaser';
import { topScores } from '../storage/high-scores';
import { audio } from '../audio';
import { isTouchDevice } from '../input/device';
import { Button } from '../ui/button';

export class TitleScene extends Phaser.Scene {
  private title!: Phaser.GameObjects.Text;
  private tagline!: Phaser.GameObjects.Text;
  private scoresText!: Phaser.GameObjects.Text;
  private playBtn!: Button;
  private helpBtn!: Button;
  private muteBtn!: Button;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    this.title = this.add
      .text(0, 0, 'Marshmallow Madness', {
        fontFamily: 'system-ui',
        fontSize: '52px',
        color: '#ffb347',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const controls = isTouchDevice() ? 'tap & drag to move' : 'arrow keys / W·S to move';
    this.tagline = this.add
      .text(0, 0, `How long can you toast without burning?\n${controls}`, {
        fontFamily: 'system-ui',
        fontSize: '18px',
        color: '#b89a6a',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5);

    this.scoresText = this.add
      .text(0, 0, this.scoreboard(), {
        fontFamily: 'monospace',
        fontSize: '15px',
        color: '#e9d5ae',
        align: 'center',
        lineSpacing: 4,
      })
      .setOrigin(0.5, 0);

    this.playBtn = new Button(this, 0, 0, '▶  Play', () => {
      // First user gesture — unlock audio before gameplay starts.
      void audio.resumeOnGesture();
      this.scene.start('PlayScene');
    }, {
      fill: 0x7a3f16,
      fillHover: 0x9a521e,
      fontSize: 26,
    });
    this.helpBtn = new Button(this, 0, 0, 'How to play', () => window.open('./help.html', '_blank'), {
      width: 180,
      fontSize: 18,
    });
    this.muteBtn = new Button(this, 0, 0, this.muteLabel(), () => {
      void audio.resumeOnGesture();
      audio.toggleMute();
      this.muteBtn.setText(this.muteLabel());
    }, { width: 180, fontSize: 18 });

    this.layout();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.scale.off(Phaser.Scale.Events.RESIZE, this.layout, this),
    );
  }

  private muteLabel(): string {
    return audio.isMuted() ? '🔇 Sound: off' : '🔊 Sound: on';
  }

  private scoreboard(): string {
    const scores = topScores(10);
    if (scores.length === 0) return 'No runs yet — be the first to toast!';
    const rows = scores.map((s, i) => {
      const rank = String(i + 1).padStart(2);
      const score = String(s.score).padStart(6);
      const done = String(s.marshmallowsDone).padStart(3);
      return `${rank}.  ${score} pts   ${done} toasted`;
    });
    return `— best runs —\n${rows.join('\n')}`;
  }

  private layout(): void {
    const { width, height } = this.scale.gameSize;
    const cx = width / 2;

    // Scale the title down to fit narrow (phone-portrait) viewports.
    this.title.setScale(1);
    const maxTitleW = width * 0.92;
    if (this.title.width > maxTitleW) this.title.setScale(maxTitleW / this.title.width);

    this.title.setPosition(cx, height * 0.16);
    this.tagline.setPosition(cx, height * 0.16 + this.title.displayHeight * 0.62 + 14);
    this.scoresText.setPosition(cx, height * 0.32);
    this.playBtn.setPosition(cx, height * 0.74);

    // Side-by-side help/mute, split scaled so they never run off a narrow screen.
    const split = Math.min(100, width * 0.26);
    this.helpBtn.setPosition(cx - split, height * 0.87);
    this.muteBtn.setPosition(cx + split, height * 0.87);
  }
}
